import { Box, Button, FormControl, FormLabel, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useMutation } from "react-query";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import salesService from "../../services/sales";
import queryDefault from "../../utils/queryDefault";

export default function TestOcr() {
  const notification = useNotification();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [expectedText, setExpectedText] = useState("");
  const [responseData, setResponseData] = useState<any>(null);

  const testOcrMutation = useMutation("test-ocr", salesService.testOcr, {
    onSuccess: (response) => {
      setResponseData(response);
      notification.onOpen({
        message: "OCR test request sent successfully",
        type: "success",
        position: "top",
      });
    },
    onError: (error: any) => {
      setResponseData(error?.response?.data || { message: "Something went wrong" });
      notification.onOpen({
        message: error?.response?.data?.message || "Something went wrong",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;

    if (!file) {
      setImageFile(null);
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!["jpg", "jpeg", "png"].includes(extension || "")) {
      notification.onOpen({
        message: "Please upload image file only (.jpg, .jpeg, .png)",
        type: "error",
        position: "top",
      });
      event.target.value = "";
      setImageFile(null);
      return;
    }

    setImageFile(file);
  };

  const handleSend = () => {
    if (!imageFile) {
      notification.onOpen({
        message: "Image is required",
        type: "error",
        position: "top",
      });
      return;
    }

    if (!expectedText.trim()) {
      notification.onOpen({
        message: "Expected text is required",
        type: "error",
        position: "top",
      });
      return;
    }

    const formData = new FormData();
    formData.append("image", imageFile);
    formData.append("expected_text", expectedText);

    testOcrMutation.mutate(formData);
  };

  return (
    <MainLayout>
      <Breadcrumb
        label="Test OCR"
        breadcrumbs={[{ label: "Sales", href: "/app/sales" }]}
      />

      <ProtectedPermissionPage acceptPermissions={[1]}>
        <Box sx={{ mt: 3 }}>
          <Stack spacing={2} sx={{ maxWidth: 500 }}>
            <FormControl>
              <FormLabel required>Image File</FormLabel>
              <TextField
                type="file"
                inputProps={{ accept: ".jpg,.jpeg,.png,image/jpeg,image/png" }}
                onChange={handleFileChange}
              />
            </FormControl>

            <FormControl>
              <FormLabel required>Expected Text (Number)</FormLabel>
              <TextField
                type="number"
                value={expectedText}
                onChange={(event) => setExpectedText(event.target.value)}
                placeholder="Input expected number"
              />
            </FormControl>

            <Box>
              <Button
                variant="contained"
                onClick={handleSend}
                disabled={testOcrMutation.isLoading}
              >
                Send
              </Button>
            </Box>
          </Stack>

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Raw Response JSON
            </Typography>
            <Box
              component="pre"
              sx={{
                p: 2,
                m: 0,
                bgcolor: "#f5f5f5",
                borderRadius: 1,
                overflow: "auto",
                fontSize: 13,
              }}
            >
              {responseData ? JSON.stringify(responseData, null, 2) : "No response yet"}
            </Box>
          </Box>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}